import React, { useState } from "react";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import CustomTable from "@components/CustomTable/CustomTable";
import Loader from "@components/Loader/Loader";
import CustomActionMenu from "@components/CustomActionMenu/CustomActionMenu";
import PreviewPdfModal from "@components/PreviewPdfModal/PreviewPdfModal";
import UpdateModal from "@components/UpdateModal/UpdateModal";
import CreditNotePreviewPDF from "@components/PreviewPdfModal/CreditNotePreviewPdf";
import {
  useLazyGetInvoiceByIdQuery,
  useUpdateInvoiceMutation,
  useUpdateInvoiceStatusMutation,
} from "store/invoice/invoiceApi";
import { useLazyGetOfferByIdQuery } from "store/offer/offerApi";
import { useGetAvoirByInvoiceNumQuery } from "store/avoir/avoir.api";
import "./index.scss";

const InvoiceList = ({ invoices, isAvoir }) => {
  const currentUser = useSelector((state) => state.auth.user);
  const { t } = useTranslation();

  const [updateInvoiceStatus] = useUpdateInvoiceStatusMutation();
  const [updateInvoice] = useUpdateInvoiceMutation();
  const [modalInvoice, setModalInvoice] = useState(null);
  const [modalFields, setModalFields] = useState({});
  const [showPreview, setShowPreview] = useState(false);
  const [previewInvoiceData, setPreviewInvoiceData] = useState(null);
  const [showAvoirModal, setShowAvoirModal] = useState(false);
  const [selectedAvoir, setSelectedAvoir] = useState(null);
  const [showReceipt, setShowReceipt] = useState(false);
  const [receiptInvoice, setReceiptInvoice] = useState(null);
  const [offersData, setOffersData] = useState([]);

  const user = useSelector((state) => state.auth.user);
  const companyId = user?.companyId?._id;
  // 👉 NEW : état pour la modale d’avoir

  const [getOfferById] = useLazyGetOfferByIdQuery();
  const canUpdateInvoice =
    currentUser?.roleId?.permissions.includes("UPDATE_INVOICE");
  const canUpdatePayment =
    currentUser?.roleId?.permissions.includes("UPDATE_INVOICE");

  // Mapping statuts français
  const invoiceStatusMap = {
    Brouillon: "Brouillon",
    "A valider": "À valider",
    Validé: "Validé",
    Publié: "Publié",
  };

  const paymentStatusMap = {
    paid: "Payé",
    partially_paid: "Partiellement payé",
    unpaid: "Non payé",
  };

  const handleStatusChange = (id, newStatus) => {
    updateInvoiceStatus({ id, invoiceStatus: newStatus });
  };

  const handlePaymentStatusChange = async (invoiceId, newStatus) => {
    try {
      await updateInvoice({ id: invoiceId, paymentStatus: newStatus }).unwrap();
      toast.success("Statut mis à jour !");
    } catch (error) {
      toast.error("Erreur lors de la mise à jour du statut");
    }
  };

  const handleShowReceipt = (invoice) => {
    setReceiptInvoice(invoice);
    setShowReceipt(true);
  };

  const handleOpenModal = (invoice) => {
    setModalInvoice(invoice);
    setModalFields({
      numeroInvoice: invoice?.numeroInvoice,
      clientName: invoice?.clientName || invoice.client?.name || "",
      totalHT: invoice?.totalHT,
      dateDeFacturation: invoice?.dateDeFacturation,
      clientId: invoice?.client?._id || invoice.clientId || "",
      invoice: invoice,
    });
  };

  const handleCloseModal = () => setModalInvoice(null);

  const handleConsultInvoice = (invoice) => {
    setPreviewInvoiceData({
      ...invoice,
      offers: invoice.offers.length
        ? invoice.offers
        : [
            {
              nom: "",
              description: "",
              price: 0,
              quantity: 0,
              TVA: 0,
              reduction: 0,
              TotalHt: 0,
            },
          ],
      sellerName: invoice.sellerName || "Default Seller",
      clientName:
        invoice.clientName || invoice.client?.name || "Default Client",
      totalHT: invoice.totalHT || 0,
    });
    setShowPreview(true);
  };

  const handleModalFieldChange = (e) => {
    setModalFields({ ...modalFields, [e.target.name]: e.target.value });
  };

  const handleModalSave = async (updatedInvoice) => {
    if (!modalInvoice) return;
    try {
      await updateInvoice({
        id: updatedInvoice?._id,
        ...updatedInvoice,
        companyId:
          typeof updatedInvoice.companyId === "object"
            ? updatedInvoice.companyId._id
            : updatedInvoice.companyId,
      }).unwrap();
      toast.success("Facture mise à jour avec succès !");
      handleCloseModal();
    } catch (error) {
      toast.error("Erreur lors de la mise à jour de la facture");
      console.error("Update error:", error);
    }
  };

  const headers = [
    { key: "numero", label: t("invoice.column_ref") },
    ...(isAvoir
      ? [{ key: "numeroInvoice", label: t("invoice.column_ref_invoice") }]
      : []),
    { key: "client", label: t("invoice.column_client") },
    { key: "paymentStatus", label: t("invoice.column_payment_status") },
    { key: "totalTTC", label: t("invoice.column_total_ttc") },
    { key: "dateDeFacturation", label: t("invoice.column_invoice_date") },
    { key: "invoiceStatus", label: t("invoice.column_invoice_status") },
    { key: "avoirs", label: t("invoice.column_credit_notes") },
  ];

  const AvoirCell = ({ invoice }) => {
    const companyId =
      typeof invoice.companyId === "object"
        ? invoice.companyId._id
        : invoice.companyId;
    const {
      data: avoirData,
      isLoading,
      error,
    } = useGetAvoirByInvoiceNumQuery(
      { companyId, invoiceId: invoice._id },
      { skip: !companyId || !invoice._id || isAvoir }
    );

    if (isLoading) return <span>Chargement...</span>;
    if (error || !avoirData?.data) return <span>-</span>;

    const avoir = avoirData.data;
    const handleClick = () => {
      setSelectedAvoir(avoir);
      setShowAvoirModal(true);
    };

    return (
      <span
        onClick={handleClick}
        style={{
          color: "#2563eb",
          fontWeight: "bold",
          cursor: "pointer",
          textDecoration: "underline",
        }}
        title={`Avoir: ${avoir?.numeroCreditNote}`}>
        {avoir?.numeroCreditNote}
      </span>
    );
  };

  const renderCell = (key, invoice) => {
    if (key === "avoirs") return <AvoirCell invoice={invoice} />;
    if (key === "numero")
      return isAvoir ? invoice.numeroCreditNote : invoice.numeroInvoice;
    if (key === "client")
      return invoice?.clientId?.name || invoice.client?.name;

    if (key === "paymentStatus") {
      return canUpdatePayment ? (
        <select
          value={invoice.paymentStatus}
          onChange={(e) =>
            handlePaymentStatusChange(invoice._id, e.target.value)
          }
          className="table-input border-0"
          style={{
            fontWeight: "bold",
            color:
              invoice.paymentStatus === "paid"
                ? "green"
                : invoice.paymentStatus === "partially_paid"
                ? "#f59e0b"
                : "#ef4444",
          }}>
          <option value="paid">{t("invoice.paid_status")}</option>
          <option value="partially_paid">{t("invoice.partially_paid")}</option>
          <option value="unpaid"> {t("invoice.unpaid")}</option>
        </select>
      ) : (
        <span
          style={{
            fontWeight: "bold",
            color:
              invoice.paymentStatus === "paid"
                ? "green"
                : invoice.paymentStatus === "partially_paid"
                ? "#f59e0b"
                : "#ef4444",
          }}>
          {paymentStatusMap[invoice.paymentStatus] || invoice.paymentStatus}
        </span>
      );
    }

    if (key === "invoiceStatus") {
      return canUpdateInvoice ? (
        <select
          value={invoice.invoiceStatus || ""}
          onChange={(e) =>
            invoice.invoiceStatus !== "Publié" &&
            handleStatusChange(invoice._id, e.target.value)
          }
          disabled={invoice.invoiceStatus === "Publié"}
          style={{
            border: "none",
            background: "transparent",
            fontWeight: "bold",
            color:
              invoice.invoiceStatus === "Brouillon"
                ? "#eab308"
                : invoice.invoiceStatus === "A valider"
                ? "#f97316"
                : invoice.invoiceStatus === "Validé"
                ? "green"
                : invoice.invoiceStatus === "Publié"
                ? "#2563eb"
                : "#333",
          }}>
          <option value="Brouillon">{t("invoice.draftt")}</option>
          <option value="A valider">{t("invoice.to_validate")}</option>
          <option value="Validé">{t("invoice.validated")}</option>
          <option value="Publié">{t("invoice.published")}</option>
        </select>
      ) : (
        <span
          style={{
            fontWeight: "bold",
            color:
              invoice.invoiceStatus === "Brouillon"
                ? "#eab308"
                : invoice.invoiceStatus === "A valider"
                ? "#f97316"
                : invoice.invoiceStatus === "Validé"
                ? "green"
                : invoice.invoiceStatus === "Publié"
                ? "#2563eb"
                : "#333",
          }}>
          {invoiceStatusMap[invoice.invoiceStatus] || invoice.invoiceStatus}
        </span>
      );
    }

    if (key === "dateDeFacturation")
      return invoice.dateDeFacturation
        ? new Date(invoice.dateDeFacturation).toLocaleDateString("fr-FR")
        : "";

    if (key === "totalTTC") {
      const ttc = invoice.total || invoice.totalHT + invoice.taxTVA;
      return `${ttc.toFixed(2)} ${invoice.devise || "€"}`;
    }

    return invoice[key];
  };

  const actionMenu = (invoice) => {
    let actions = [
      {
        label: t("invoice.consult"),
        onClick: () => handleConsultInvoice(invoice),
      },
    ];

    if (canUpdateInvoice && invoice.invoiceStatus !== "Publié") {
      actions.unshift({
        label: t("invoice.edit"),
        onClick: () => handleOpenModal(invoice),
      });
    }

    if (invoice.paymentStatus !== "unpaid") {
      actions.push({
        label: t("invoice.view_receipt"),
        onClick: () => handleShowReceipt(invoice),
      });
    }

    return <CustomActionMenu data={invoice} actions={actions} />;
  };

  return (
    <div style={{ overflowX: "auto" }}>
      {invoices ? (
        <CustomTable
          headers={headers}
          data={invoices}
          rowKey="_id"
          renderCell={renderCell}
          actionMenu={actionMenu}
        />
      ) : (
        <Loader />
      )}

      {/* Modal modifier facture */}
      <UpdateModal
        handleCloseModal={handleCloseModal}
        handleModalFieldChange={handleModalFieldChange}
        handleModalSave={handleModalSave}
        modalFields={modalFields}
        modalInvoice={modalInvoice}
        title={"Modifier la facture"}
      />

      {/* Modal preview facture */}
      <PreviewPdfModal
        previewInvoiceData={previewInvoiceData}
        setShowPreview={setShowPreview}
        showPreview={showPreview}
        title={t("invoice.invoice")}
      />

      {/* Modal preview avoir */}
      <PreviewPdfModal
        previewInvoiceData={previewInvoiceData}
        setShowPreview={setShowAvoirModal}
        showPreview={showAvoirModal}
        isAvoir={true}
        title={t("avoir.title_pdf_preview")}
        previewPdf={
          <CreditNotePreviewPDF
            creditNoteData={selectedAvoir}
            companyId={companyId}
          />
        }
      />
    </div>
  );
};

export default InvoiceList;
